import {useEffect, useState} from 'react';
import {Trans} from '@ui/i18n/trans';
import {Button} from '@ui/buttons/button';
import {Link} from 'react-router';
import type {BlockInstance} from '../appearance-data';
import {AmbientOrbs, Parallax, Reveal} from '../motion';
import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {useSelectedLocale} from '@ui/i18n/selected-locale';

/** Hero section with optional typing animation. Props are already localized. */
export function HeroBlock({props}: {block: BlockInstance; props: Record<string, any>}) {
  const align = props.align === 'start' ? 'text-start items-start' : 'text-center items-center';
  const phrases: string[] = props.enable_typing
    ? String(props.typing_phrases || '').split('\n').map(s => s.trim()).filter(Boolean)
    : [];

  // A heading may carry a highlighted segment after a "|" — e.g.
  // "Your Gateway to|Global Opportunities" renders the 2nd part in the
  // brand gradient (matches the design's two-tone hero title).
  const [headMain, headHi] = String(props.heading || '').split('|');
  // A tag is "Label", "Label|tab", or "Label|tab|search-or-filters".
  //
  // The third part exists because a label is written for a human and a query
  // is matched against titles, and those are rarely the same string. "Turkey
  // Scholarships" reads well on a pill and matches no scholarship title, so
  // sending the label as the search term found nothing. Now the label can stay
  // readable while the pill actually searches for "Turkey" — or skips the text
  // search entirely and sets filters, in the same `key=value&key=value` form
  // the Advanced Search block already puts in the URL and this page already
  // reads back:
  //
  //   Erasmus                                        → search everywhere
  //   Remote Jobs|job                                → job tab, search "Remote Jobs"
  //   Turkey Scholarships|scholarship|Turkey         → scholarship tab, search "Turkey"
  //   Study in Germany|program|country_code=DE       → program tab, filtered, no text search
  const tags = String(props.tags || '')
    .split(/[\n,]/).map(s => s.trim()).filter(Boolean)
    .map(s => {
      const [label, tab, target] = s.split('|').map(x => x.trim());
      return {label, tab: tab || '', target: target || ''};
    });

  // Auto tags: the listings we sell services on, most used first. On by
  // default; when it returns nothing (or is off) the typed tags are used.
  const {localeCode} = useSelectedLocale();
  const autoOn = props.auto_tags !== false && props.auto_tags !== 0 && props.auto_tags !== '0';
  const {data: autoTags} = useQuery({
    queryKey: ['hero-popular-tags', localeCode, props.auto_tags_count],
    queryFn: () =>
      apiClient
        .get('public/opp/popular-tags', {params: {lang: localeCode, limit: Number(props.auto_tags_count) || 6}})
        .then(r => (r.data?.tags ?? []) as {label: string; href: string}[]),
    enabled: autoOn,
    staleTime: 10 * 60_000,
  });
  const useAuto = autoOn && !!autoTags?.length;

  /** Where one pill goes. */
  const tagHref = (t: {label: string; tab: string; target: string; href?: string}) => {
    if (t.href) return t.href;
    const params = new URLSearchParams();
    if (t.tab) params.set('tab', t.tab);

    if (t.target.includes('=')) {
      // Filters. Parsed rather than concatenated so a stray space or a missing
      // value cannot produce a URL the search page silently ignores.
      new URLSearchParams(t.target).forEach((value, key) => {
        if (key && value) params.set(key, value);
      });
    } else {
      // A search term: the third part when given, the label otherwise.
      params.set('query', t.target || t.label);
    }

    return `/opportunities?${params.toString()}`;
  };
  const badges = Array.isArray(props.badges) ? props.badges.filter((b: any) => b && (b.value || b.label)) : [];
  const hasImage = !!props.image;
  // Light hero (split, dark text) vs the classic full-bleed dark hero.
  const split = hasImage;
  const dark = !split && (props.text_color || '#fff').toLowerCase() !== '#0f172a';

  const eyebrow = props.eyebrow ? (
    <span className="mb-2 inline-flex items-center gap-6 rounded-full bg-primary/10 px-14 py-6 text-xs font-semibold text-primary">
      <span className="material-icons text-sm">stars</span>
      {props.eyebrow}
    </span>
  ) : null;

  const heading = props.heading ? (
    <h1 className="text-3xl font-bold leading-tight md:text-[44px] md:leading-[1.1]">
      {headMain}
      {headHi ? (
        <>
          {' '}
          <span className="bg-gradient-to-r from-primary to-[#7c3aed] bg-clip-text text-transparent">{headHi.trim()}</span>
        </>
      ) : null}
    </h1>
  ) : null;

  const buttons = (
    <div className="mt-8 flex flex-wrap items-center gap-12" style={{justifyContent: split || props.align === 'start' ? 'flex-start' : 'center'}}>
      {props.button_label ? (
        <Button variant="flat" color="primary" size="lg" className="ap-shine rounded-full shadow-lg shadow-primary/25"
          elementType={Link} to={props.button_url || '/'}>{props.button_label}</Button>
      ) : null}
      {props.button2_label ? (
        <Button variant="outline" size="lg" className={`rounded-full ${dark ? 'border-white/60 text-white' : ''}`}
          elementType={Link} to={props.button2_url || '/'}>{props.button2_label}</Button>
      ) : null}
    </div>
  );

  const shownTags: {label: string; tab: string; target: string; href?: string}[] = useAuto
    ? autoTags!.map(t => ({label: t.label, tab: '', target: '', href: t.href}))
    : tags;
  const tagsRow = shownTags.length ? (
    <div className="flex flex-wrap items-center gap-8 text-sm">
      {/* Was a bare literal, so it stayed English on an Arabic homepage
          while the tags beside it translated. */}
      <span className="font-medium opacity-70">
        <Trans message="Popular:" />
      </span>
      {shownTags.map((t, i) => (
        <Link key={i}
          to={tagHref(t)}
          className="rounded-full bg-black/5 px-10 py-2 text-xs font-medium transition hover:bg-primary/10 hover:text-primary">
          {t.label}
        </Link>
      ))}
    </div>
  ) : null;

  const typingRow = phrases.length > 0 ? (
    <p data-typing className="text-xl font-semibold md:text-3xl" style={{minHeight: '1.4em'}}>
      <Typewriter phrases={phrases} />
    </p>
  ) : null;

  // ── Split layout (text left, image + floating stat badges right) ──────
  if (split) {
    return (
      <section
        className={`relative isolate overflow-hidden px-24 py-48 md:py-72 ${
          // No explicit colour → use the dark-mode-aware `.ap-hero-split`
          // ground (light lavender / dark charcoal) and let text follow the
          // theme, so the split hero doesn't flash a white band in dark mode.
          props.bg_color ? '' : 'ap-hero-split text-main'
        }`}
        style={props.bg_color
          ? {background: `linear-gradient(135deg, ${props.bg_color} 0%, #ffffff 70%)`, color: props.text_color || '#0f172a'}
          : props.text_color ? {color: props.text_color} : undefined}
      >
        {/* Drifting colour orbs give the flat gradient some depth and life. */}
        <AmbientOrbs />
        <div className="mx-auto grid max-w-[1180px] items-center gap-40 md:grid-cols-2">
          <div className="flex flex-col items-start gap-16 text-start">
            <Reveal from="left"><div className="flex flex-col items-start gap-16">
              {eyebrow}
              {heading}
            </div></Reveal>
            <Reveal from="left" index={1}><div className="flex flex-col items-start gap-16">
              {typingRow}
              {props.subheading ? <p className="max-w-[560px] text-lg opacity-80">{props.subheading}</p> : null}
            </div></Reveal>
            <Reveal from="left" index={2}><div className="flex flex-col items-start gap-16">
              {tagsRow}
              {buttons}
            </div></Reveal>
          </div>
          {/* The photo drifts slightly against the scroll and slowly pushes in
              (Ken Burns), so the hero keeps moving without being distracting. */}
          <Parallax speed={-40} className="relative mx-auto w-full max-w-[460px]">
            <div className="relative">
              <div className="ap-zoom overflow-hidden rounded-[28px] shadow-2xl ring-1 ring-black/5">
                <img src={props.image} alt="" className="ap-ken h-full w-full object-cover" />
              </div>
              {/* Floating stat badges around the photo. A defined border + ring +
                  strong shadow (and a frosted backdrop) keep them clearly readable
                  over light image areas or the pale gradient, not just dark spots.
                  Each bobs on its own offset so they don't move in lockstep. */}
              {badges.slice(0, 3).map((b: any, i: number) => (
                <div key={i}
                  className={`ap-float ap-delay-${i + 1} absolute flex items-center gap-8 whitespace-nowrap rounded-2xl border border-black/5 bg-white/90 px-14 py-10 shadow-xl ring-1 ring-black/5 backdrop-blur-md ${
                    i === 0 ? '-top-6 right-16' : i === 1 ? 'top-1/3 -left-10' : '-bottom-6 right-24'
                  }`}>
                  <span className="text-lg font-bold text-primary">{b.value}</span>
                  <span className="text-xs font-medium text-slate-500">{b.label}</span>
                </div>
              ))}
            </div>
          </Parallax>
        </div>
      </section>
    );
  }

  // ── Classic centered / full-bleed hero ───────────────────────────────
  return (
    <section
      className="relative isolate overflow-hidden px-24 py-64 md:py-96"
      style={{backgroundColor: props.bg_color || '#0b3d2e', color: props.text_color || '#fff'}}
    >
      <div className={`mx-auto flex max-w-[960px] flex-col gap-16 ${align}`}>
        {eyebrow}
        {heading}
        {typingRow}
        {props.subheading ? <p className="max-w-720 text-lg opacity-90">{props.subheading}</p> : null}
        {tagsRow}
        {buttons}
      </div>
    </section>
  );
}

/** Cycles through phrases with a type-in / type-out effect. */
function Typewriter({phrases}: {phrases: string[]}) {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const full = phrases[index % phrases.length] || '';
    const done = !deleting && text === full;
    const cleared = deleting && text === '';
    const delay = done ? 1400 : cleared ? 200 : deleting ? 40 : 70;

    const t = setTimeout(() => {
      if (done) setDeleting(true);
      else if (cleared) {
        setDeleting(false);
        setIndex(i => i + 1);
      } else {
        setText(full.slice(0, deleting ? text.length - 1 : text.length + 1));
      }
    }, delay);
    return () => clearTimeout(t);
  }, [text, deleting, index, phrases]);

  return (
    <span>
      {text}
      <span className="ms-1 inline-block w-2 animate-pulse" style={{borderInlineEnd: '2px solid currentColor'}}>&nbsp;</span>
    </span>
  );
}
